import { useContext, useState, useEffect } from 'react';
import styles from './CompanyHeader.module.css';
import AppContext from '../../AppContext';
import RatioFormat from '../format/RatioFormat';
import ISiteLink from '../links/ISiteLink';
import CompanyHeaderData from './CompanyHeaderData';
import Environment from '../../Environment';
import Alert from '../Alert/Alert';
import { AlertType } from '../Alert/AlertType';
import CloudAppsLink from '../links/CloudAppsLink';
import { cachedFetch } from '../../utils/cachedFetch';

function CompanyHeader() {
  const { cocode, year, periodType, setEntityId, statementType, setSecurityCIK, setGroupCode } = useContext(AppContext);
  const [companyHeaderData, setCompanyHeaderData] = useState<CompanyHeaderData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const url = periodType
      ? `${Environment.API_URL()}/company_header/${cocode}/${year}?periodType=${periodType}`
      : `${Environment.API_URL()}/company_header/${cocode}/${year}`;
    cachedFetch(url, {
      headers: {
        Authorization: `${sessionStorage.getItem('bearerToken')}`
      }
    }).then((res) => {
      if (!res.ok) {
        res.json().then((body) => {
          setError(body.message);
        });
      } else {
        res.json().then(
          (data: CompanyHeaderData) => {
            setCompanyHeaderData(data);
            setEntityId(data.entityId);
            setSecurityCIK(data.securityCIK);
            setGroupCode(data.groupCode);
          },
          (error) => {
            console.log('Error in request: ', error);
          }
        );
      }
    });
  }, [cocode, year, periodType, setEntityId, setSecurityCIK]);

  return (
    <>
      {error && <Alert alertType={AlertType.DANGER} content={error} />}
      <div className={styles.header}>
        <div id="company-header" className={styles.company_data}>
          <label data-testid="company-name">
            Company Name:{' '}
            {companyHeaderData ? (
              <ISiteLink
                relativeUrl={`/codemographics/CompanyDemo?run_mode=INFO&sCategoryName=Demographics&year=${year}&cocode=${cocode}&sEntityID=${
                  companyHeaderData.entityId
                }&sEntityName=${encodeURIComponent(companyHeaderData.companyName)}&sEntityType=FF`}
                label={companyHeaderData.companyName}
              />
            ) : (
              <span>N/A</span>
            )}
          </label>
          <label>
            Domicile:{' '}
            <span data-testid="state-of-domicile">
              {companyHeaderData?.stateOfDomicile || 'N/A'}
            </span>
          </label>
          <label>
            Statement Type:{' '}
            <span data-testid="statement-type">{companyHeaderData?.statementType || 'N/A'}</span>
          </label>
          <label>
            Year:{' '}
            <span id="report-year" data-testid="report-year">
              {year || 'N/A'}
            </span>
          </label>
          {periodType && (
            <label data-testid="filing-period">
              Filing Period:{' '}
              <span>{companyHeaderData?.filingPeriod || 'N/A'}</span>
            </label>
          )}
          <label data-testid="latest-exam">
            Latest Exam:{' '}
            <ISiteLink
              relativeUrl={`/feets-web/spring/feets/viewIndividualExam?cocode=${cocode}`}
              label={companyHeaderData?.latestExam}
            />
          </label>
          <label>
            Cocode:{' '}
            <span id="report-cocode" data-testid="report-cocode">
              {cocode || 'N/A'}
            </span>
          </label>
          <label data-testid="group-code">
            Group Code:{' '}
            {companyHeaderData?.groupCode ? (
              <ISiteLink
                relativeUrl={`/common/GroupList?year=${year}&grpcode=${companyHeaderData?.groupCode}`}
                label={companyHeaderData?.groupCode}
              />
            ) : (
              <span>N/A</span>
            )}
          </label>
          <label>
            Company Status:{' '}
            <span data-testid="company-status">{companyHeaderData?.companyStatus || 'N/A'}</span>
          </label>
          <label>
            Nationally Significant:{' '}
            <span data-testid="nationally-significant">
              {companyHeaderData?.nationallySignificant || 'N/A'}
            </span>
          </label>
          <label data-testid="permitted-prescribed-practice">
            Perm./Presc. Practice:{' '}
            {companyHeaderData?.permittedPrescribedPractice === 'YES' ? (
              [
                <ISiteLink
                  key="permitted-prescribed-practice-report"
                  relativeUrl={`/pick-a-page/PickAPage?REPORT=${statementType?.statementTypeCode}${year}NSIGACCTPOL//N^4&COCODE=${statementType?.statementTypeCode}${cocode}&DATA_YEAR=${year}&filetype=HTML`}
                  label="YES"
                />,
                ' / ',
                <CloudAppsLink
                  key="permitted-prescribed-practice-database"
                  relativeUrl={`/suite/sites/ppa?cocode=${cocode}`}
                  label="Database"
                />
              ]
            ) : (
              <span>NO</span>
            )}
          </label>
          <label data-testid="group-name">
            Group Name: <span>{companyHeaderData?.groupName || 'N/A'}</span>
          </label>
          <label data-testid="lead-state">
            Lead State:{' '}
            {companyHeaderData?.leadState ? (
              <ISiteLink
                relativeUrl={`/lead-state/LeadStateCriteria`}
                label={companyHeaderData?.leadState}
              />
            ) : (
              <span>N/A</span>
            )}
          </label>
          <label data-testid="company-type">
            Company Type: <span>{companyHeaderData?.companyType || 'N/A'}</span>
          </label>
          <label data-testid="rbc-ratio">
            RBC Ratio:{' '}
            <span>{companyHeaderData && <RatioFormat value={companyHeaderData.rbcRatio} />}</span>
          </label>
          <label data-testid="filing-amended">
            Filing Amended:{' '}
            {companyHeaderData?.filingAmended === 'YES' ? (
              <ISiteLink
                relativeUrl={`/filing-pdfs/ViewDocs?DATA_YEAR=${year}&COCODE=${statementType?.statementTypeCode}${cocode}`}
                label="YES"
              />
            ) : (
              <span>NO</span>
            )}
          </label>
        </div>
      </div>
    </>
  );
}

export default CompanyHeader;
